import { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import {
  type ApolloError,
  ConversationType,
  MessagePartType,
  useAskMutation,
  useCreateConversationMutation,
} from '@matrix-forge/graphql-client';
import { toast } from '@matrix-forge/ui/components/sonner';
import { formatDate } from '../utils/datetime';
import { AllClientSideTools } from '../tools/all';
import { setPendingAsk } from '../utils/pendingAsks';
import type { ClientTool } from './useClientTools';

export type UseNewChatOptions = {
  onNewChat?: ((args: { conversationId: string; agentId: string }) => any) | null;
  /** Additional client tools to include (e.g. persona tools for Agent-type agents). */
  extraClientTools?: ClientTool[];
};

/**
 * Creates a new conversation with a given agent id and asks a given prompt.
 *
 * When `onNewChat` is provided (the navigation case), the `ask` mutation is deferred
 * until `useMessageStream` fires it from within the conversation component — after
 * Apollo's subscription setup effect has run. This eliminates the race window where
 * events published by the backend during `ask` could be missed because the subscription
 * WebSocket hadn't been established yet. `useMessageStream` guarantees ordering via
 * React's effect sequencing (its effect runs after the subscription effect).
 *
 * When `onNewChat` is absent (no navigation), `ask` fires immediately as before.
 */
export const useNewChat = (opts?: UseNewChatOptions | null) => {
  const onNewChat = opts?.onNewChat ?? null;
  const extraClientTools = opts?.extraClientTools;

  const allTools = useMemo(
    () =>
      extraClientTools?.length
        ? [...AllClientSideTools, ...extraClientTools]
        : AllClientSideTools,
    [extraClientTools]
  );

  const [createConversation, createState] = useCreateConversationMutation();
  const [ask] = useAskMutation();
  const trigger = useCallback(
    async ({ agentId, prompt }: { agentId: string; prompt: string }) => {
      try {
        const clientTools = allTools.map(tool => ({
          name: tool.name,
          description: tool.description,
          parameters: JSON.stringify(tool.parameters),
        }));

        const createResponse = await createConversation({
          variables: {
            input: {
              title: `Chat started ${formatDate(new Date())}`,
              type: ConversationType.Chat,
              clientTools,
            },
          },
        });

        const newConversationId = createResponse?.data?.createConversation?.id;
        if (!newConversationId) {
          throw Error('Invalid response when creating new conversation.');
        }

        const result = { conversationId: newConversationId, agentId, prompt };

        if (onNewChat) {
          // Deferred path: store the ask so useMessageStream fires it after the
          // subscription is established. Navigation happens first so the WebSocket
          // subscribe message is in-flight before ask reaches the server.
          setPendingAsk(newConversationId, () =>
            ask({
              variables: {
                input: {
                  conversationId: newConversationId,
                  agentId,
                  clientTools,
                  parts: [{ type: MessagePartType.Text, text: prompt ?? '' }],
                },
              },
            }).catch(error => {
              const err = error as ApolloError;
              toast.error('Something went wrong starting a new chat.', {
                id: 'new-chat',
                description: `${String(err?.message || '')}${err?.message ? ' ' : ''}Please try again.`,
              });
              Sentry.captureException(error, {
                tags: { errorType: 'mutation', mutationType: 'ask', component: 'useNewChat' },
                extra: { mutationVariables: { agentId, prompt } },
              });
              throw error;
            })
          );
          onNewChat(result);
          return result;
        }

        // Non-navigation path: fire ask immediately (original behaviour).
        await ask({
          variables: {
            input: {
              conversationId: newConversationId,
              agentId,
              clientTools,
              parts: [{ type: MessagePartType.Text, text: prompt ?? '' }],
            },
          },
        });
        return result;
      } catch (error) {
        const err = error as ApolloError;
        toast.error('Something went wrong starting a new chat.', {
          id: 'new-chat',
          description: `${String(err?.message || '')}${err?.message ? ' ' : ''}Please try again.`,
        });
        Sentry.captureException(error, {
          tags: {
            errorType: 'mutation',
            mutationType: 'createConversation | ask',
            component: 'useNewChat',
          },
          extra: { mutationVariables: { agentId, prompt } },
        });
        throw error;
      }
    },
    [createConversation, ask, allTools, onNewChat]
  );

  const loading = !createState.error && createState.loading;
  return useMemo<[typeof trigger, { loading: boolean; error?: ApolloError }]>(
    () => [trigger, { loading, error: createState.error }],
    [trigger, loading, createState.error]
  );
};
